import React, {
  createContext,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { getSortedTracks } from "~/js/Views/TracksManager/Sort/sortHelper";
import { ImportContext } from "~/Context/ImportContext";

export const FilteredTracksContext = createContext();

export const FilteredTracksProvider = ({ children }) => {
  const { tracksIndex } = useContext(ImportContext);

  const [sortOptions, setSortOptions] = useState({
    sortBy: "info.name",
    sortOrder: "asc",
  });
  const [filterActivities, setFilterActivities] = useState([]);
  const [filterWaypoints, setFilterWaypoints] = useState([]);
  const [filterFavorites, setFilterFavorites] = useState([]);
  const [filterGpxTypes, setFilterGpxTypes] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const handleFilterTracks = useCallback(
    (
      tracks,
      activitiesToFilter,
      waypointsToFilter,
      favoritesToFilter,
      gpxTypesToFilter,
      groupsToFilter,
      query,
    ) => {
      let filtered = tracks;

      if (query) {
        const lowerQuery = query.toLowerCase();
        filtered = filtered.filter((track) => {
          const name = track["info.name"] || "";
          return name.toLowerCase().includes(lowerQuery);
        });
      }

      if (activitiesToFilter && activitiesToFilter.length > 0) {
        filtered = filtered.filter((track) =>
          activitiesToFilter.includes(track["info.activity.id"]),
        );
      }

      if (waypointsToFilter && waypointsToFilter.length > 0) {
        const showWith = waypointsToFilter.includes("hasWaypoints");
        const showWithout = waypointsToFilter.includes("noWaypoints");

        if (showWith && !showWithout) {
          filtered = filtered.filter((track) => track.wayPointsCount > 0);
        } else if (!showWith && showWithout) {
          filtered = filtered.filter(
            (track) => !track.wayPointsCount || track.wayPointsCount === 0,
          );
        }
      }

      if (favoritesToFilter && favoritesToFilter.length > 0) {
        const showFavorites = favoritesToFilter.includes("isFavorite");
        if (showFavorites) {
          filtered = filtered.filter(
            (track) =>
              track["info.favorite"] === true ||
              track["info.favorite"] === "true",
          );
        }
      }

      if (gpxTypesToFilter && gpxTypesToFilter.length > 0) {
        filtered = filtered.filter((track) => {
          return gpxTypesToFilter.every((type) => {
            switch (type) {
              case "elevation":
                return (
                  track["stats.elevation.ascent"] !== null ||
                  track["stats.elevation.descent"] !== null
                );
              case "heartrate":
                return track["stats.heartrate.average"] !== null;
              case "speed":
                return (
                  track["stats.speed.average"] !== null ||
                  track["stats.duration.total"] > 0
                );
              case "temperature":
                return track["stats.temperature.average"] !== null;
              default:
                return true;
            }
          });
        });
      }

      if (groupsToFilter && groupsToFilter.length > 0) {
        filtered = filtered.filter((track) =>
          groupsToFilter.includes(track["info.groupId"]),
        );
      }

      return filtered;
    },
    [],
  );

  const filteredTracks = useMemo(() => {
    let sortedList = [];
    if (tracksIndex.length > 0) {
      sortedList = handleFilterTracks(
        tracksIndex,
        filterActivities,
        filterWaypoints,
        filterFavorites,
        filterGpxTypes,
        filterGroups,
        searchQuery,
      );
    } else {
      sortedList = tracksIndex;
    }
    return getSortedTracks(
      sortedList,
      sortOptions.sortBy,
      sortOptions.sortOrder,
    );
  }, [
    tracksIndex,
    filterActivities,
    filterWaypoints,
    filterFavorites,
    filterGpxTypes,
    filterGroups,
    sortOptions,
    handleFilterTracks,
    searchQuery,
  ]);

  const value = {
    isFiltered: tracksIndex.length !== filteredTracks.length,
    filteredTracks,
    sortOptions,
    setSortOptions,
    filterActivities,
    setFilterActivities,
    filterWaypoints,
    setFilterWaypoints,
    filterFavorites,
    setFilterFavorites,
    filterGpxTypes,
    setFilterGpxTypes,
    filterGroups,
    setFilterGroups,
    searchQuery,
    setSearchQuery,
    handleFilterTracks,
  };

  return (
    <FilteredTracksContext.Provider value={value}>
      {children}
    </FilteredTracksContext.Provider>
  );
};
